import { Either, isLeft, left, right } from 'fp-ts/Either';
import { IPasswordPolicy, PASSWORD_POLICY } from 'src/config/config';
import { useCallback } from 'react';
import { RuleObject, StoreValue } from 'rc-field-form/lib/interface';

interface Options extends IPasswordPolicy {}

export class PasswordLengthError extends Error {}
export class PasswordCharactersError extends Error {}

/**
 * Validate if password is strong enough to use.
 * A little easier to customize than plain Regexp we had before
 */
export function validatePassword(password: string, options: Options): Either<Error, true> {
  const {
    minLength,
    maxLength,
    include: { numbers, symbols, lowercase, uppercase }
  } = options;
  if (minLength && password.length < minLength) {
    return left(new PasswordLengthError('too short'));
  }
  if (maxLength && password.length > maxLength) {
    return left(new PasswordLengthError('too long'));
  }
  if (numbers && password.match(/[0-9]/) === null) {
    return left(new PasswordCharactersError('should contains at least one number'));
  }
  if (symbols && password.match(/[!@#$%^&*()+_\-=}{[\]|:;"/?.><,`~]/) === null) {
    return left(new PasswordCharactersError('should contains at least one special symbol'));
  }
  if (lowercase && password.match(/[a-z]/) === null) {
    return left(new PasswordCharactersError('should contains at least one lowercase letter'));
  }
  if (uppercase && password.match(/[A-Z]/) === null) {
    return left(new PasswordCharactersError('should contains at least one uppercase letter'));
  }
  return right(true);
}

export function usePasswordValidator(): (rule: RuleObject, value: StoreValue) => Promise<void> {
  return useCallback(async (rule: RuleObject, value: StoreValue): Promise<void> => {
    const { message } = rule;
    if (typeof message !== 'string') {
      throw new Error('Incorrect validator');
    }

    const result = validatePassword(value, PASSWORD_POLICY);
    if (isLeft(result)) {
      throw new Error(message);
    }
  }, []);
}

export default usePasswordValidator;
